import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Code2, Sun, Moon, LogOut, User, BookOpen, ChevronRight, ChevronDown,
  Grid3X3, List, Search, Filter, GraduationCap, Briefcase, ArrowUpDown,
  Building2, Stethoscope, Calculator, Palette, Wrench, Leaf, Shield,
  Rocket, Home, Award, Clock, Users, Star, Play, ArrowLeft, X, IndianRupee, Trophy
} from 'lucide-react';
import { coursesApi, courseRequestsApi, gradeAllocationsApi } from '../lib/api';
import { toast } from 'sonner';
import { TeacherAvatar } from '../components/TeacherAvatar';

// Career Images - Generated illustrations for each career category
const CAREER_IMAGES = {
  // Engineering
  'Mechanical Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/1f7e8ad77ec52c34cf0236d0b9ecb7b07ef0f5b7668162e91e3279f2a950dcb7.png',
  'Civil Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/1f7e8ad77ec52c34cf0236d0b9ecb7b07ef0f5b7668162e91e3279f2a950dcb7.png',
  'Electrical Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/5071094a4f437258dedc49452b0a6721cfaaf41d7fcb3605ab42d3be50d45863.png',
  'Electronics Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/5071094a4f437258dedc49452b0a6721cfaaf41d7fcb3605ab42d3be50d45863.png',
  'Chemical Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/1f7e8ad77ec52c34cf0236d0b9ecb7b07ef0f5b7668162e91e3279f2a950dcb7.png',
  'Automobile Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/1f7e8ad77ec52c34cf0236d0b9ecb7b07ef0f5b7668162e91e3279f2a950dcb7.png',
  'Agricultural Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/2d71044b06235dc5f53153b2cce7866edf05310e3e1d0781a7f55ecd0a31f47e.png',
  // IT & Tech
  'Software Engineer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/2e868dab1599a7efc327a0fce8da07647141d2ff090ca4d9fd352ce84e2dba2c.png',
  'Data Analyst': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/2e868dab1599a7efc327a0fce8da07647141d2ff090ca4d9fd352ce84e2dba2c.png',
  'Business Analyst': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  // Architecture
  'Architect': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/eb7a9a6874e10536b4e3d0aa0fa596883f463ad0d83f10d2d72ec4205475c9ea.png',
  // Healthcare
  'Doctor (MBBS)': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/4bef8485b5b766ce823341089996da64309700be5361f527b0a6887e0a2c9a43.png',
  'Nurse': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/f5806a8d7b1ee6aa5df43d7a353e3c21dd183eadd855d7cf265126c3c391e338.png',
  'Pharmacist': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/4bef8485b5b766ce823341089996da64309700be5361f527b0a6887e0a2c9a43.png',
  'Lab Technician': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/4bef8485b5b766ce823341089996da64309700be5361f527b0a6887e0a2c9a43.png',
  'Physiotherapist': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/f5806a8d7b1ee6aa5df43d7a353e3c21dd183eadd855d7cf265126c3c391e338.png',
  // Finance & Business
  'Chartered Accountant': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  'Accountant': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  'Bank Officer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  'HR Executive': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  'Marketing Executive': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  // Creative & Hospitality
  'Graphic Designer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/829362e031d21910f1348adc07fa9af0f9192c7e1549e8548f808243089c81d4.png',
  'Interior Designer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/eb7a9a6874e10536b4e3d0aa0fa596883f463ad0d83f10d2d72ec4205475c9ea.png',
  'Fashion Designer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/829362e031d21910f1348adc07fa9af0f9192c7e1549e8548f808243089c81d4.png',
  'Chef': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/86c3820c292081f73b5ce7adf8160b1e9a37b702bc31bef20d515f160e845abb.png',
  'Photographer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/829362e031d21910f1348adc07fa9af0f9192c7e1549e8548f808243089c81d4.png',
  'Event Manager': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/829362e031d21910f1348adc07fa9af0f9192c7e1549e8548f808243089c81d4.png',
  // Skilled Trades
  'Plumber': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/5071094a4f437258dedc49452b0a6721cfaaf41d7fcb3605ab42d3be50d45863.png',
  'Electrician': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/5071094a4f437258dedc49452b0a6721cfaaf41d7fcb3605ab42d3be50d45863.png',
  'Welder': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/1f7e8ad77ec52c34cf0236d0b9ecb7b07ef0f5b7668162e91e3279f2a950dcb7.png',
  'Painter': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/829362e031d21910f1348adc07fa9af0f9192c7e1549e8548f808243089c81d4.png',
  'Carpenter': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/1f7e8ad77ec52c34cf0236d0b9ecb7b07ef0f5b7668162e91e3279f2a950dcb7.png',
  'AC Technician': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/5071094a4f437258dedc49452b0a6721cfaaf41d7fcb3605ab42d3be50d45863.png',
  'Machine Operator': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/1f7e8ad77ec52c34cf0236d0b9ecb7b07ef0f5b7668162e91e3279f2a950dcb7.png',
  // Agriculture
  'Farmer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/2d71044b06235dc5f53153b2cce7866edf05310e3e1d0781a7f55ecd0a31f47e.png',
  'Agriculture Officer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/2d71044b06235dc5f53153b2cce7866edf05310e3e1d0781a7f55ecd0a31f47e.png',
  'Dairy Manager': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/2d71044b06235dc5f53153b2cce7866edf05310e3e1d0781a7f55ecd0a31f47e.png',
  // Government & Defense
  'Police Officer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/a0516ec98a2a6656daea9388b97ad1d1a2d8ecc7953db5725381a89c4fe50ccd.png',
  'Army Officer': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/a0516ec98a2a6656daea9388b97ad1d1a2d8ecc7953db5725381a89c4fe50ccd.png',
  'Teacher': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  'Professor': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/ba15378fac826cafbec8928ca7e491308b026e7644f2cbaa7b8f29486d55238f.png',
  // Entrepreneurship
  'Entrepreneur': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/b5edeb1cfc7f83c6a472cab020be10fafbaa85ffc20661cd06a6d96e6e7893bd.png',
  'Startup Founder': 'https://static.prod-images.emergentagent.com/jobs/4bf787b8-e0a0-45b9-b33e-d5c769298613/images/b5edeb1cfc7f83c6a472cab020be10fafbaa85ffc20661cd06a6d96e6e7893bd.png',
};

// Career data with categories - Expanded descriptions (75-100 words) with employer information
const CAREER_DATA = {
  engineering: {
    name: 'Engineering',
    icon: Building2,
    color: 'from-blue-500 to-blue-700',
    careers: [
      { 
        title: 'Mechanical Engineer', 
        qualification: 'Diploma / B.E. / B.Tech', 
        description: 'Mechanical engineers are problem-solvers who design, develop, and test mechanical devices and systems. They work across diverse industries including automotive, aerospace, energy, and manufacturing. Their responsibilities include creating blueprints using CAD software, analyzing thermal and fluid systems, selecting materials, and ensuring machines operate efficiently. They collaborate with cross-functional teams to bring innovative products from concept to production while meeting safety standards and sustainability goals.',
        skills: ['CAD/CAM', 'Thermodynamics', 'Materials Science', 'Problem Solving'], 
        salary: '₹4-15 LPA',
        employers: [
          { name: 'Tata Motors', logo: 'https://logo.clearbit.com/tatamotors.com' },
          { name: 'Mahindra', logo: 'https://logo.clearbit.com/mahindra.com' },
          { name: 'L&T', logo: 'https://logo.clearbit.com/larsentoubro.com' },
          { name: 'Maruti Suzuki', logo: 'https://logo.clearbit.com/marutisuzuki.com' },
          { name: 'BHEL', logo: 'https://logo.clearbit.com/bhel.com', type: 'govt' }
        ]
      },
      { 
        title: 'Civil Engineer', 
        qualification: 'Diploma / B.E. / B.Tech', 
        description: 'Civil engineers shape the world we live in by designing and overseeing construction of essential infrastructure. They plan buildings, bridges, highways, water supply systems, and urban developments. Using structural analysis and surveying techniques, they ensure safety and durability. Civil engineers work with architects, contractors, and government agencies, balancing aesthetics with functionality. They play a crucial role in sustainable development and disaster-resistant construction practices.',
        skills: ['Structural Analysis', 'AutoCAD', 'Project Management', 'Surveying'], 
        salary: '₹4-12 LPA',
        employers: [
          { name: 'L&T Construction', logo: 'https://logo.clearbit.com/larsentoubro.com' },
          { name: 'DLF', logo: 'https://logo.clearbit.com/dlf.in' },
          { name: 'NHAI', logo: 'https://logo.clearbit.com/nhai.gov.in', type: 'govt' },
          { name: 'PWD', logo: '', type: 'govt' },
          { name: 'Shapoorji Pallonji', logo: 'https://logo.clearbit.com/shapoorji.in' }
        ]
      },
      { 
        title: 'Electrical Engineer', 
        qualification: 'Diploma / B.E. / B.Tech', 
        description: 'Electrical engineers work on systems that generate, transmit, and utilize electrical power. They design circuits, develop power systems, and create electronic devices. From renewable energy installations to smart grid technologies, they ensure reliable electricity supply. They troubleshoot electrical issues, maintain equipment, and implement automation solutions. Their expertise is essential in power plants, manufacturing units, and building electrical infrastructure for modern cities.',
        skills: ['Circuit Design', 'Power Systems', 'Control Systems', 'MATLAB'], 
        salary: '₹4-14 LPA',
        employers: [
          { name: 'NTPC', logo: 'https://logo.clearbit.com/ntpc.co.in', type: 'govt' },
          { name: 'Power Grid', logo: 'https://logo.clearbit.com/powergrid.in', type: 'govt' },
          { name: 'Siemens', logo: 'https://logo.clearbit.com/siemens.com' },
          { name: 'ABB', logo: 'https://logo.clearbit.com/abb.com' },
          { name: 'Schneider Electric', logo: 'https://logo.clearbit.com/se.com' }
        ]
      },
      { 
        title: 'Electronics Engineer', 
        qualification: 'Diploma / B.E. / B.Tech', 
        description: 'Electronics engineers design and develop electronic systems, from tiny microchips to complex communication networks. They work on PCB design, embedded systems, and semiconductor technologies. Their innovations power smartphones, medical devices, satellites, and IoT systems. They conduct testing, debug circuits, and optimize performance. With the rise of AI and 5G, electronics engineers are at the forefront of technological advancement, making devices smarter and more efficient.',
        skills: ['PCB Design', 'Embedded Systems', 'VLSI', 'Signal Processing'], 
        salary: '₹4-16 LPA',
        employers: [
          { name: 'Samsung', logo: 'https://logo.clearbit.com/samsung.com' },
          { name: 'Intel', logo: 'https://logo.clearbit.com/intel.com' },
          { name: 'Qualcomm', logo: 'https://logo.clearbit.com/qualcomm.com' },
          { name: 'ISRO', logo: 'https://logo.clearbit.com/isro.gov.in', type: 'govt' },
          { name: 'DRDO', logo: 'https://logo.clearbit.com/drdo.gov.in', type: 'govt' }
        ]
      },
      { 
        title: 'Chemical Engineer', 
        qualification: 'B.E. / B.Tech', 
        description: 'Chemical engineers transform raw materials into valuable products through chemical processes. They work in pharmaceuticals, petrochemicals, food processing, and environmental management. Their responsibilities include designing reactors, optimizing production processes, and ensuring safety compliance. They develop sustainable manufacturing methods and waste treatment solutions. Chemical engineers contribute to medicines, fuels, plastics, and clean water, making them essential for industrial progress and environmental protection.',
        skills: ['Process Design', 'Chemical Processes', 'Safety Management', 'Quality Control'], 
        salary: '₹5-15 LPA',
        employers: [
          { name: 'Reliance Industries', logo: 'https://logo.clearbit.com/ril.com' },
          { name: 'Indian Oil', logo: 'https://logo.clearbit.com/iocl.com', type: 'govt' },
          { name: 'ONGC', logo: 'https://logo.clearbit.com/ongcindia.com', type: 'govt' },
          { name: 'BASF', logo: 'https://logo.clearbit.com/basf.com' },
          { name: 'Dr. Reddy\'s', logo: 'https://logo.clearbit.com/drreddys.com' }
        ]
      },
      { 
        title: 'Automobile Engineer', 
        qualification: 'Diploma / B.Tech', 
        description: 'Automobile engineers design, develop, and manufacture vehicles ranging from cars and trucks to motorcycles and electric vehicles. They work on engine systems, chassis design, aerodynamics, and safety features. With the EV revolution, they focus on battery technology, autonomous driving systems, and sustainable mobility. They conduct crash tests, improve fuel efficiency, and integrate smart technologies. Automobile engineers shape the future of transportation and mobility solutions.',
        skills: ['Vehicle Dynamics', 'Engine Design', 'Automotive Electronics', 'EV Technology'], 
        salary: '₹4-18 LPA',
        employers: [
          { name: 'Tata Motors', logo: 'https://logo.clearbit.com/tatamotors.com' },
          { name: 'Mahindra', logo: 'https://logo.clearbit.com/mahindra.com' },
          { name: 'Maruti Suzuki', logo: 'https://logo.clearbit.com/marutisuzuki.com' },
          { name: 'Hyundai', logo: 'https://logo.clearbit.com/hyundai.com' },
          { name: 'Bajaj Auto', logo: 'https://logo.clearbit.com/bajajauto.com' }
        ]
      },
      { 
        title: 'Agricultural Engineer', 
        qualification: 'B.Tech', 
        description: 'Agricultural engineers combine engineering principles with agricultural sciences to improve farming efficiency. They design irrigation systems, farm machinery, and food processing equipment. Their work includes developing soil conservation methods, greenhouse automation, and precision farming technologies. They address challenges like water scarcity and crop storage. Agricultural engineers help farmers increase yields while reducing environmental impact, playing a vital role in food security and rural development.',
        skills: ['Farm Machinery', 'Irrigation Design', 'Soil Science', 'Food Processing'], 
        salary: '₹4-10 LPA',
        employers: [
          { name: 'ICAR', logo: 'https://logo.clearbit.com/icar.org.in', type: 'govt' },
          { name: 'IARI', logo: '', type: 'govt' },
          { name: 'John Deere', logo: 'https://logo.clearbit.com/deere.com' },
          { name: 'Mahindra Agri', logo: 'https://logo.clearbit.com/mahindra.com' },
          { name: 'State Agriculture Dept', logo: '', type: 'govt' }
        ]
      },
    ]
  },
  it_tech: {
    name: 'IT & Technology',
    icon: Code2,
    color: 'from-violet-500 to-purple-700',
    careers: [
      { 
        title: 'Software Engineer', 
        qualification: 'B.Tech / BCA / B.Sc IT', 
        description: 'Software engineers build the digital world by designing, developing, and maintaining software applications. They write clean, efficient code using programming languages like Python, Java, and JavaScript. From mobile apps to enterprise systems, they solve complex problems through algorithmic thinking. They participate in agile sprints, conduct code reviews, and deploy applications to cloud platforms. Software engineers continuously learn new technologies, making them essential in today\'s digital economy.',
        skills: ['Programming', 'Data Structures', 'System Design', 'Problem Solving'], 
        salary: '₹5-40 LPA',
        employers: [
          { name: 'TCS', logo: 'https://logo.clearbit.com/tcs.com' },
          { name: 'Infosys', logo: 'https://logo.clearbit.com/infosys.com' },
          { name: 'Wipro', logo: 'https://logo.clearbit.com/wipro.com' },
          { name: 'Google', logo: 'https://logo.clearbit.com/google.com' },
          { name: 'Microsoft', logo: 'https://logo.clearbit.com/microsoft.com' }
        ]
      },
      { 
        title: 'Data Analyst', 
        qualification: 'B.Sc / BCA / B.Tech', 
        description: 'Data analysts transform raw data into actionable insights that drive business decisions. They collect, clean, and analyze datasets using SQL, Python, and Excel. Creating visualizations and dashboards, they communicate trends to stakeholders. They identify patterns, forecast outcomes, and measure KPIs. From e-commerce to healthcare, data analysts help organizations understand customer behavior, optimize operations, and gain competitive advantages through evidence-based decision making.',
        skills: ['SQL', 'Python', 'Excel', 'Data Visualization'], 
        salary: '₹4-18 LPA',
        employers: [
          { name: 'Amazon', logo: 'https://logo.clearbit.com/amazon.com' },
          { name: 'Flipkart', logo: 'https://logo.clearbit.com/flipkart.com' },
          { name: 'Deloitte', logo: 'https://logo.clearbit.com/deloitte.com' },
          { name: 'Accenture', logo: 'https://logo.clearbit.com/accenture.com' },
          { name: 'Mu Sigma', logo: 'https://logo.clearbit.com/mu-sigma.com' }
        ]
      },
      { 
        title: 'Business Analyst', 
        qualification: 'MBA / B.Tech', 
        description: 'Business analysts bridge the gap between technology and business objectives. They gather requirements from stakeholders, document processes, and propose solutions that improve efficiency. Using tools like JIRA and Confluence, they create user stories and functional specifications. They facilitate communication between technical teams and business users, ensuring projects meet organizational goals. Business analysts drive digital transformation by identifying automation opportunities and measuring project success.',
        skills: ['Requirements Analysis', 'Documentation', 'Communication', 'Process Mapping'], 
        salary: '₹6-25 LPA',
        employers: [
          { name: 'Cognizant', logo: 'https://logo.clearbit.com/cognizant.com' },
          { name: 'Capgemini', logo: 'https://logo.clearbit.com/capgemini.com' },
          { name: 'IBM', logo: 'https://logo.clearbit.com/ibm.com' },
          { name: 'KPMG', logo: 'https://logo.clearbit.com/kpmg.com' },
          { name: 'EY', logo: 'https://logo.clearbit.com/ey.com' }
        ]
      },
    ]
  },
  architecture: {
    name: 'Architecture & Design',
    icon: Home,
    color: 'from-amber-500 to-orange-600',
    careers: [
      { 
        title: 'Architect', 
        qualification: 'B.Arch', 
        description: 'Architects envision and design buildings that blend aesthetics with functionality. They create detailed blueprints using AutoCAD and 3D modeling software, considering structural integrity, sustainability, and user experience. From residential homes to commercial complexes, they coordinate with engineers and contractors throughout construction. Architects balance client requirements with building codes and environmental considerations. Their creative vision shapes skylines and living spaces, leaving a lasting impact on communities and urban landscapes.',
        skills: ['AutoCAD', '3D Modeling', 'Design Thinking', 'Project Management'], 
        salary: '₹5-20 LPA',
        employers: [
          { name: 'CPWD', logo: 'https://logo.clearbit.com/cpwd.gov.in', type: 'govt' },
          { name: 'DLF', logo: 'https://logo.clearbit.com/dlf.in' },
          { name: 'Godrej Properties', logo: 'https://logo.clearbit.com/godrejproperties.com' },
          { name: 'Hafeez Contractor', logo: '' },
          { name: 'Morphogenesis', logo: 'https://logo.clearbit.com/morphogenesis.org' }
        ]
      },
    ]
  },
  healthcare: {
    name: 'Healthcare',
    icon: Stethoscope,
    color: 'from-emerald-500 to-teal-600',
    careers: [
      { 
        title: 'Doctor (MBBS)', 
        qualification: 'MBBS', 
        description: 'Doctors are healthcare professionals who diagnose diseases, prescribe treatments, and guide patients toward recovery. After completing MBBS, they can specialize through MD/MS in fields like cardiology, orthopedics, or pediatrics. They examine patients, interpret medical tests, perform procedures, and provide preventive care. Doctors work in hospitals, clinics, and rural health centers, often making life-saving decisions under pressure. Their dedication to patient welfare makes them respected pillars of society.',
        skills: ['Clinical Skills', 'Diagnosis', 'Patient Care', 'Medical Knowledge'], 
        salary: '₹8-50 LPA',
        employers: [
          { name: 'AIIMS', logo: 'https://logo.clearbit.com/aiims.edu', type: 'govt' },
          { name: 'Apollo Hospitals', logo: 'https://logo.clearbit.com/apollohospitals.com' },
          { name: 'Fortis Healthcare', logo: 'https://logo.clearbit.com/fortishealthcare.com' },
          { name: 'Max Healthcare', logo: 'https://logo.clearbit.com/maxhealthcare.in' },
          { name: 'Govt. Medical Colleges', logo: '', type: 'govt' }
        ]
      },
      { 
        title: 'Nurse', 
        qualification: 'B.Sc Nursing / GNM', 
        description: 'Nurses are the backbone of healthcare, providing compassionate patient care around the clock. They administer medications, monitor vital signs, assist in surgeries, and educate patients about health management. Working in hospitals, clinics, and home care settings, they coordinate with doctors and support staff. Nurses specialize in areas like ICU, pediatrics, or oncology. Their empathy, attention to detail, and clinical skills make them indispensable in the healing process.',
        skills: ['Patient Care', 'Medical Procedures', 'Communication', 'Emergency Response'], 
        salary: '₹3-10 LPA',
        employers: [
          { name: 'AIIMS', logo: 'https://logo.clearbit.com/aiims.edu', type: 'govt' },
          { name: 'Apollo Hospitals', logo: 'https://logo.clearbit.com/apollohospitals.com' },
          { name: 'Narayana Health', logo: 'https://logo.clearbit.com/narayanahealth.org' },
          { name: 'Govt. Hospitals', logo: '', type: 'govt' },
          { name: 'WHO/NGOs', logo: 'https://logo.clearbit.com/who.int', type: 'govt' }
        ]
      },
      { 
        title: 'Pharmacist', 
        qualification: 'D.Pharm / B.Pharm', 
        description: 'Pharmacists are medication experts who ensure safe and effective drug therapy. They dispense prescriptions, counsel patients on proper usage, and check for drug interactions. Working in retail pharmacies, hospitals, and pharmaceutical companies, they manage inventory and comply with regulations. Pharmacists also contribute to drug research, clinical trials, and public health initiatives. Their knowledge prevents medication errors and helps patients achieve better health outcomes.',
        skills: ['Pharmacology', 'Drug Interactions', 'Patient Counseling', 'Inventory Management'], 
        salary: '₹3-12 LPA',
        employers: [
          { name: 'Apollo Pharmacy', logo: 'https://logo.clearbit.com/apollopharmacy.in' },
          { name: 'MedPlus', logo: 'https://logo.clearbit.com/medplusmart.com' },
          { name: 'Cipla', logo: 'https://logo.clearbit.com/cipla.com' },
          { name: 'Sun Pharma', logo: 'https://logo.clearbit.com/sunpharma.com' },
          { name: 'Govt. Hospitals', logo: '', type: 'govt' }
        ]
      },
      { 
        title: 'Lab Technician', 
        qualification: 'Diploma', 
        description: 'Lab technicians perform diagnostic tests that help doctors identify diseases and monitor treatment progress. They collect blood samples, conduct analyses using sophisticated equipment, and maintain laboratory standards. From hematology to microbiology, they handle various specimen types with precision. Lab technicians ensure quality control, document results accurately, and support pathologists. Their behind-the-scenes work is crucial for accurate diagnosis and effective patient treatment planning.',
        skills: ['Lab Equipment', 'Sample Analysis', 'Quality Control', 'Documentation'], 
        salary: '₹2-6 LPA',
        employers: [
          { name: 'Dr. Lal PathLabs', logo: 'https://logo.clearbit.com/lalpathlabs.com' },
          { name: 'SRL Diagnostics', logo: 'https://logo.clearbit.com/srlworld.com' },
          { name: 'Thyrocare', logo: 'https://logo.clearbit.com/thyrocare.com' },
          { name: 'Metropolis', logo: 'https://logo.clearbit.com/metropolisindia.com' },
          { name: 'Govt. Labs', logo: '', type: 'govt' }
        ]
      },
      { 
        title: 'Physiotherapist', 
        qualification: 'BPT', 
        description: 'Physiotherapists help patients recover mobility and reduce pain through therapeutic exercises and manual techniques. They treat sports injuries, post-surgical rehabilitation, neurological conditions, and chronic pain. Using modalities like ultrasound and electrical stimulation, they create personalized treatment plans. Physiotherapists work in hospitals, sports clinics, and rehabilitation centers. Their hands-on approach and patient motivation skills help individuals regain independence and improve quality of life.',
        skills: ['Rehabilitation', 'Exercise Therapy', 'Manual Therapy', 'Patient Assessment'], 
        salary: '₹3-12 LPA',
        employers: [
          { name: 'Apollo Hospitals', logo: 'https://logo.clearbit.com/apollohospitals.com' },
          { name: 'Fortis Healthcare', logo: 'https://logo.clearbit.com/fortishealthcare.com' },
          { name: 'HealthCare at Home', logo: '' },
          { name: 'Sports Authorities', logo: '', type: 'govt' },
          { name: 'Rehabilitation Centers', logo: '' }
        ]
      },
    ]
  },
  finance_business: {
    name: 'Finance & Business',
    icon: Calculator,
    color: 'from-green-500 to-emerald-600',
    careers: [
      { 
        title: 'Chartered Accountant', 
        qualification: 'CA Qualification', 
        description: 'Chartered Accountants are financial experts who handle accounting, auditing, taxation, and advisory services. They ensure organizations comply with financial regulations and optimize tax strategies. CAs prepare financial statements, conduct audits, and provide business consultancy. Working in accounting firms, corporations, or as independent practitioners, they advise on mergers, investments, and financial planning. The CA qualification is rigorous, making CAs highly respected and well-compensated professionals.',
        skills: ['Accounting', 'Taxation', 'Auditing', 'Financial Analysis'], 
        salary: '₹7-40 LPA',
        employers: [
          { name: 'Deloitte', logo: 'https://logo.clearbit.com/deloitte.com' },
          { name: 'PwC', logo: 'https://logo.clearbit.com/pwc.com' },
          { name: 'EY', logo: 'https://logo.clearbit.com/ey.com' },
          { name: 'KPMG', logo: 'https://logo.clearbit.com/kpmg.com' },
          { name: 'CAG Office', logo: '', type: 'govt' }
        ]
      },
      { 
        title: 'Accountant', 
        qualification: 'B.Com', 
        description: 'Accountants manage financial records, ensuring accuracy and compliance with accounting standards. They handle bookkeeping, prepare balance sheets, process payroll, and file tax returns. Using software like Tally and QuickBooks, they track income and expenses. Accountants work in businesses of all sizes, from startups to corporations. Their attention to detail prevents financial discrepancies and supports informed business decisions. Many accountants advance to senior finance roles or pursue CA certification.',
        skills: ['Bookkeeping', 'Tally', 'GST', 'Financial Reporting'], 
        salary: '₹3-8 LPA',
        employers: [
          { name: 'Any Corporate', logo: '' },
          { name: 'CA Firms', logo: '' },
          { name: 'Banks', logo: '' },
          { name: 'Govt. Departments', logo: '', type: 'govt' },
          { name: 'SMEs', logo: '' }
        ]
      },
      { 
        title: 'Bank Officer', 
        qualification: 'Graduation + Competitive Exam', 
        description: 'Bank officers manage banking operations, from processing loans to handling customer accounts. They assess creditworthiness, approve transactions, and promote financial products. Working in public or private banks, they ensure regulatory compliance and prevent fraud. Through exams like IBPS and SBI PO, candidates enter this stable career. Bank officers advance to branch managers and regional heads, enjoying job security, benefits, and opportunities to serve customers and grow professionally.',
        skills: ['Banking Operations', 'Customer Service', 'Financial Products', 'Compliance'], 
        salary: '₹5-15 LPA',
        employers: [
          { name: 'SBI', logo: 'https://logo.clearbit.com/sbi.co.in', type: 'govt' },
          { name: 'HDFC Bank', logo: 'https://logo.clearbit.com/hdfcbank.com' },
          { name: 'ICICI Bank', logo: 'https://logo.clearbit.com/icicibank.com' },
          { name: 'PNB', logo: 'https://logo.clearbit.com/pnbindia.in', type: 'govt' },
          { name: 'RBI', logo: 'https://logo.clearbit.com/rbi.org.in', type: 'govt' }
        ]
      },
      { 
        title: 'HR Executive', 
        qualification: 'BBA / MBA', 
        description: 'HR executives manage the employee lifecycle from recruitment to exit. They source candidates, conduct interviews, handle onboarding, and maintain employee records. Managing payroll, benefits, and compliance, they ensure workplace harmony. HR executives organize training programs, resolve conflicts, and implement company policies. They use HRMS software for efficient operations. As strategic partners, they shape organizational culture and help businesses attract and retain top talent.',
        skills: ['Recruitment', 'Employee Relations', 'HRMS', 'Labor Laws'], 
        salary: '₹4-15 LPA',
        employers: [
          { name: 'TCS', logo: 'https://logo.clearbit.com/tcs.com' },
          { name: 'Infosys', logo: 'https://logo.clearbit.com/infosys.com' },
          { name: 'Reliance', logo: 'https://logo.clearbit.com/ril.com' },
          { name: 'Tata Group', logo: 'https://logo.clearbit.com/tata.com' },
          { name: 'Any Corporate', logo: '' }
        ]
      },
      { 
        title: 'Marketing Executive', 
        qualification: 'Graduation', 
        description: 'Marketing executives create and implement strategies to promote products and build brand awareness. They conduct market research, plan campaigns, and manage digital marketing channels including social media, SEO, and email marketing. Analyzing consumer behavior and campaign performance, they optimize marketing ROI. They coordinate with creative teams and agencies. Marketing executives drive customer acquisition and engagement, playing a key role in business growth and brand positioning.',
        skills: ['Digital Marketing', 'Brand Management', 'Analytics', 'Communication'], 
        salary: '₹4-20 LPA',
        employers: [
          { name: 'HUL', logo: 'https://logo.clearbit.com/hul.co.in' },
          { name: 'ITC', logo: 'https://logo.clearbit.com/itcportal.com' },
          { name: 'Ogilvy', logo: 'https://logo.clearbit.com/ogilvy.com' },
          { name: 'Dentsu', logo: 'https://logo.clearbit.com/dentsu.com' },
          { name: 'Amazon', logo: 'https://logo.clearbit.com/amazon.com' }
        ]
      },
    ]
  },
  creative: {
    name: 'Creative & Hospitality',
    icon: Palette,
    color: 'from-pink-500 to-rose-600',
    careers: [
      { 
        title: 'Graphic Designer', 
        qualification: 'Diploma / Degree', 
        description: 'Graphic designers create visual content that communicates messages effectively. Using tools like Photoshop, Illustrator, and Figma, they design logos, marketing materials, websites, and social media graphics. They understand color theory, typography, and layout principles. Working in agencies, in-house teams, or freelancing, they collaborate with clients to bring creative visions to life. Graphic designers shape brand identities and make complex information visually appealing and accessible.',
        skills: ['Photoshop', 'Illustrator', 'UI/UX', 'Branding'], 
        salary: '₹3-15 LPA',
        employers: [
          { name: 'Ogilvy', logo: 'https://logo.clearbit.com/ogilvy.com' },
          { name: 'Dentsu', logo: 'https://logo.clearbit.com/dentsu.com' },
          { name: 'Flipkart', logo: 'https://logo.clearbit.com/flipkart.com' },
          { name: 'Zomato', logo: 'https://logo.clearbit.com/zomato.com' },
          { name: 'Freelance Platforms', logo: '' }
        ]
      },
      { 
        title: 'Interior Designer', 
        qualification: 'Diploma / Degree', 
        description: 'Interior designers transform spaces into functional and aesthetically pleasing environments. They plan layouts, select materials, and coordinate with contractors to execute designs. From residential homes to corporate offices and hospitality venues, they consider lighting, acoustics, and user flow. Using AutoCAD and 3D visualization tools, they present concepts to clients. Interior designers balance creativity with practicality, creating spaces that enhance well-being and reflect client personalities.',
        skills: ['Space Planning', 'AutoCAD', '3D Visualization', 'Material Selection'], 
        salary: '₹3-12 LPA',
        employers: [
          { name: 'Livspace', logo: 'https://logo.clearbit.com/livspace.com' },
          { name: 'HomeLane', logo: 'https://logo.clearbit.com/homelane.com' },
          { name: 'Architecture Firms', logo: '' },
          { name: 'Real Estate Companies', logo: '' },
          { name: 'Hospitality Chains', logo: '' }
        ]
      },
      { 
        title: 'Fashion Designer', 
        qualification: 'Diploma / Degree', 
        description: 'Fashion designers create clothing, accessories, and footwear that define style trends. They sketch designs, select fabrics, and oversee production processes. Understanding fashion history and market trends, they develop seasonal collections. Fashion designers work for apparel brands, boutiques, or launch their own labels. They participate in fashion weeks and collaborate with manufacturers. Their creativity and business acumen shape what people wear, influencing culture and self-expression.',
        skills: ['Sketching', 'Pattern Making', 'Textile Knowledge', 'Trend Analysis'], 
        salary: '₹3-20 LPA',
        employers: [
          { name: 'NIFT Placements', logo: 'https://logo.clearbit.com/nift.ac.in', type: 'govt' },
          { name: 'Fabindia', logo: 'https://logo.clearbit.com/fabindia.com' },
          { name: 'Myntra', logo: 'https://logo.clearbit.com/myntra.com' },
          { name: 'Raymond', logo: 'https://logo.clearbit.com/raymond.in' },
          { name: 'Own Label/Boutique', logo: '' }
        ]
      },
      { 
        title: 'Chef', 
        qualification: 'Hotel Management', 
        description: 'Chefs are culinary artists who create memorable dining experiences through food. They plan menus, prepare dishes, and manage kitchen operations. From fine dining restaurants to hotel chains, they ensure food quality and presentation. Chefs specialize in cuisines like Continental, Indian, or Asian. They train kitchen staff, control costs, and maintain hygiene standards. Passionate about flavors, chefs innovate with ingredients and techniques, delighting customers with every plate served.',
        skills: ['Culinary Arts', 'Menu Planning', 'Food Safety', 'Kitchen Management'], 
        salary: '₹3-15 LPA',
        employers: [
          { name: 'Taj Hotels', logo: 'https://logo.clearbit.com/tajhotels.com' },
          { name: 'ITC Hotels', logo: 'https://logo.clearbit.com/itchotels.in' },
          { name: 'Marriott', logo: 'https://logo.clearbit.com/marriott.com' },
          { name: 'Oberoi Hotels', logo: 'https://logo.clearbit.com/oberoihotels.com' },
          { name: 'Restaurants/Cafes', logo: '' }
        ]
      },
      { 
        title: 'Photographer', 
        qualification: 'Skill-based / Diploma', 
        description: 'Photographers capture moments and tell stories through images. Specializing in weddings, fashion, wildlife, products, or journalism, they use cameras and editing software to create compelling visuals. They understand lighting, composition, and post-processing techniques. Photographers work for media companies, studios, or as freelancers. Building portfolios and client relationships, they turn passion into profession. In the social media age, skilled photographers are in high demand for content creation.',
        skills: ['Photography', 'Photo Editing', 'Lighting', 'Composition'], 
        salary: '₹3-20 LPA',
        employers: [
          { name: 'Media Houses', logo: '' },
          { name: 'Wedding Studios', logo: '' },
          { name: 'E-commerce Companies', logo: '' },
          { name: 'Advertising Agencies', logo: '' },
          { name: 'Freelance', logo: '' }
        ]
      },
      { 
        title: 'Event Manager', 
        qualification: 'Graduation', 
        description: 'Event managers orchestrate memorable experiences from concept to execution. They plan weddings, corporate conferences, concerts, and exhibitions. Coordinating vendors, managing budgets, and handling logistics, they ensure events run smoothly. They create timelines, design event layouts, and manage on-ground operations. Event managers possess creativity, organizational skills, and the ability to handle pressure. Their work creates lasting memories for clients and attendees alike.',
        skills: ['Event Planning', 'Vendor Management', 'Budgeting', 'Coordination'], 
        salary: '₹4-15 LPA',
        employers: [
          { name: 'Wizcraft', logo: 'https://logo.clearbit.com/wizcraft.com' },
          { name: 'Percept', logo: 'https://logo.clearbit.com/percept.com' },
          { name: 'OML', logo: 'https://logo.clearbit.com/oml.in' },
          { name: 'Corporate Event Teams', logo: '' },
          { name: 'Wedding Planners', logo: '' }
        ]
      },
    ]
  },
  skilled_trades: {
    name: 'Skilled Trades',
    icon: Wrench,
    color: 'from-slate-500 to-gray-700',
    careers: [
      { 
        title: 'Plumber', 
        qualification: '8th–10th + ITI', 
        description: 'Plumbers install and maintain water supply, drainage, and sanitation systems in residential and commercial buildings. They read blueprints, cut and join pipes, fix leaks, and ensure proper water pressure. With growing urbanization, skilled plumbers are in high demand. They work independently or with construction companies. Plumbers troubleshoot issues, install fixtures, and maintain septic systems. Their practical skills keep water flowing and homes functioning safely.',
        skills: ['Pipe Fitting', 'Blueprint Reading', 'Problem Solving', 'Safety'], 
        salary: '₹2-6 LPA',
        employers: [
          { name: 'Construction Companies', logo: '' },
          { name: 'Housing Societies', logo: '' },
          { name: 'Municipal Corporations', logo: '', type: 'govt' },
          { name: 'Self-employed', logo: '' },
          { name: 'Maintenance Services', logo: '' }
        ]
      },
      { 
        title: 'Electrician', 
        qualification: '10th + ITI', 
        description: 'Electricians install, repair, and maintain electrical systems in buildings and industrial facilities. They wire new constructions, troubleshoot faults, and ensure electrical safety. Reading circuit diagrams and using testing equipment, they work on lighting, power outlets, and control systems. Electricians follow safety codes strictly. With increasing electrification and smart home technologies, skilled electricians find employment in construction, manufacturing, and maintenance sectors.',
        skills: ['Wiring', 'Troubleshooting', 'Safety Protocols', 'Electrical Systems'], 
        salary: '₹2-8 LPA',
        employers: [
          { name: 'L&T', logo: 'https://logo.clearbit.com/larsentoubro.com' },
          { name: 'State Electricity Boards', logo: '', type: 'govt' },
          { name: 'Construction Companies', logo: '' },
          { name: 'Manufacturing Units', logo: '' },
          { name: 'Self-employed', logo: '' }
        ]
      },
      { 
        title: 'Welder', 
        qualification: '8th–10th + ITI', 
        description: 'Welders join metal parts using various welding techniques including MIG, TIG, and arc welding. They work in construction, shipbuilding, automotive, and manufacturing industries. Reading blueprints and preparing metal surfaces, they create strong bonds essential for structural integrity. Welders operate in challenging environments, following safety protocols. Skilled welders are vital for infrastructure projects, pipeline construction, and heavy equipment manufacturing.',
        skills: ['Welding Techniques', 'Metal Work', 'Blueprint Reading', 'Safety'], 
        salary: '₹2-6 LPA',
        employers: [
          { name: 'L&T', logo: 'https://logo.clearbit.com/larsentoubro.com' },
          { name: 'Shipyards', logo: '' },
          { name: 'Oil & Gas Companies', logo: '' },
          { name: 'Auto Manufacturing', logo: '' },
          { name: 'Construction Sites', logo: '' }
        ]
      },
      { 
        title: 'Painter', 
        qualification: '8th–10th + Skill Training', 
        description: 'Painters apply paints and finishes to buildings, structures, and surfaces. They prepare surfaces by sanding, priming, and filling cracks. Using brushes, rollers, and spray equipment, they achieve smooth, even coatings. Painters work on residential, commercial, and industrial projects. They mix colors, estimate material requirements, and ensure clean finishes. Skilled painters combine technical ability with an eye for detail, transforming spaces through color and texture.',
        skills: ['Surface Preparation', 'Paint Application', 'Color Mixing', 'Safety'], 
        salary: '₹2-5 LPA',
        employers: [
          { name: 'Construction Companies', logo: '' },
          { name: 'Asian Paints Projects', logo: 'https://logo.clearbit.com/asianpaints.com' },
          { name: 'Real Estate Developers', logo: '' },
          { name: 'Interior Design Firms', logo: '' },
          { name: 'Self-employed', logo: '' }
        ]
      },
      { 
        title: 'Carpenter', 
        qualification: '8th–10th + ITI', 
        description: 'Carpenters build and repair wooden structures, furniture, and fixtures. They measure, cut, and shape wood using hand and power tools. From framing buildings to crafting custom cabinets, carpenters combine precision with creativity. They read blueprints, select appropriate materials, and install finished products. Working in construction, furniture manufacturing, or independently, carpenters create functional and beautiful wooden pieces that enhance living and working spaces.',
        skills: ['Woodworking', 'Blueprint Reading', 'Measurements', 'Tool Usage'], 
        salary: '₹2-6 LPA',
        employers: [
          { name: 'Furniture Companies', logo: '' },
          { name: 'Construction Firms', logo: '' },
          { name: 'Interior Designers', logo: '' },
          { name: 'Real Estate Projects', logo: '' },
          { name: 'Self-employed', logo: '' }
        ]
      },
      { 
        title: 'AC Technician', 
        qualification: '10th + ITI', 
        description: 'AC technicians install, maintain, and repair air conditioning and refrigeration systems. They diagnose issues, replace components, charge refrigerants, and ensure efficient cooling. Understanding electrical and mechanical systems, they work on residential and commercial HVAC units. With rising temperatures and AC adoption, skilled technicians are highly sought. They service window ACs, split units, and central air systems, keeping homes and offices comfortable year-round.',
        skills: ['HVAC Systems', 'Troubleshooting', 'Refrigeration', 'Electrical'], 
        salary: '₹2-8 LPA',
        employers: [
          { name: 'Voltas', logo: 'https://logo.clearbit.com/voltas.com' },
          { name: 'Blue Star', logo: 'https://logo.clearbit.com/bluestarindia.com' },
          { name: 'Daikin', logo: 'https://logo.clearbit.com/daikin.co.in' },
          { name: 'LG/Samsung Service', logo: '' },
          { name: 'Self-employed', logo: '' }
        ]
      },
      { 
        title: 'Machine Operator', 
        qualification: '10th + Industrial Training', 
        description: 'Machine operators run industrial machinery in manufacturing plants. They set up equipment, monitor production processes, and perform quality checks. Operating CNC machines, lathes, presses, and assembly lines, they ensure products meet specifications. Machine operators follow safety protocols, perform routine maintenance, and troubleshoot minor issues. They work in automotive, textiles, pharmaceuticals, and food processing industries, forming the backbone of manufacturing operations.',
        skills: ['Machine Operation', 'Quality Control', 'Maintenance', 'Safety'], 
        salary: '₹2-5 LPA',
        employers: [
          { name: 'Auto Component Makers', logo: '' },
          { name: 'Textile Mills', logo: '' },
          { name: 'FMCG Manufacturing', logo: '' },
          { name: 'Pharma Plants', logo: '' },
          { name: 'Heavy Industries', logo: '' }
        ]
      },
    ]
  },
  agriculture: {
    name: 'Agriculture',
    icon: Leaf,
    color: 'from-lime-500 to-green-600',
    careers: [
      { 
        title: 'Farmer', 
        qualification: 'No formal education required', 
        description: 'Farmers cultivate crops and raise livestock, forming the foundation of food security. Modern farmers use technology for precision agriculture, including drones, sensors, and data analytics. They manage soil health, irrigation, pest control, and harvest timing. Farmers navigate market dynamics, weather uncertainties, and government policies. From traditional methods to organic farming, they adapt practices for sustainability. Successful farmers combine agricultural knowledge with business acumen.',
        skills: ['Crop Management', 'Soil Science', 'Machinery', 'Marketing'], 
        salary: 'Variable',
        employers: [
          { name: 'Own Farm', logo: '' },
          { name: 'Agri-cooperatives', logo: '' },
          { name: 'Contract Farming (ITC)', logo: 'https://logo.clearbit.com/itcportal.com' },
          { name: 'Organic Farms', logo: '' },
          { name: 'FPOs', logo: '' }
        ]
      },
      { 
        title: 'Agriculture Officer', 
        qualification: 'B.Sc Agriculture', 
        description: 'Agriculture officers work with government departments to support farmers and implement agricultural schemes. They provide guidance on crop selection, pest management, and modern farming techniques. Conducting field visits, they assess conditions and recommend solutions. Agriculture officers facilitate subsidies, coordinate with research institutions, and promote sustainable practices. Their extension services connect farmers with resources, contributing to rural development and agricultural productivity.',
        skills: ['Agricultural Knowledge', 'Extension Services', 'Research', 'Policy'], 
        salary: '₹4-10 LPA',
        employers: [
          { name: 'State Agriculture Dept', logo: '', type: 'govt' },
          { name: 'NABARD', logo: 'https://logo.clearbit.com/nabard.org', type: 'govt' },
          { name: 'ICAR', logo: 'https://logo.clearbit.com/icar.org.in', type: 'govt' },
          { name: 'Krishi Vigyan Kendras', logo: '', type: 'govt' },
          { name: 'Agri-input Companies', logo: '' }
        ]
      },
      { 
        title: 'Dairy Manager', 
        qualification: 'Diploma / B.Sc', 
        description: 'Dairy managers oversee operations at dairy farms and milk processing facilities. They manage cattle health, breeding programs, and milk production. Ensuring quality standards, they supervise pasteurization, packaging, and distribution. Dairy managers handle staff, inventory, and finances. Working with cooperatives or private dairies, they implement best practices for animal welfare and product safety. Their work delivers nutritious dairy products to millions of consumers daily.',
        skills: ['Animal Husbandry', 'Dairy Technology', 'Quality Control', 'Business Management'], 
        salary: '₹3-10 LPA',
        employers: [
          { name: 'Amul', logo: 'https://logo.clearbit.com/amul.com' },
          { name: 'Mother Dairy', logo: 'https://logo.clearbit.com/motherdairy.com', type: 'govt' },
          { name: 'Nandini', logo: '', type: 'govt' },
          { name: 'Dairy Cooperatives', logo: '' },
          { name: 'Private Dairies', logo: '' }
        ]
      },
    ]
  },
  government_defense: {
    name: 'Government & Defense',
    icon: Shield,
    color: 'from-indigo-500 to-blue-700',
    careers: [
      { 
        title: 'Police Officer', 
        qualification: 'Graduation + Competitive Exam', 
        description: 'Police officers maintain law and order, protect citizens, and investigate crimes. They patrol neighborhoods, respond to emergencies, and enforce traffic rules. Through examinations like State PSC, candidates join as Sub-Inspectors or Constables. Police officers undergo rigorous training in firearms, physical fitness, and legal procedures. They serve in various departments including crime branch, traffic, and cyber crime, dedicating themselves to public safety and justice.',
        skills: ['Law Enforcement', 'Physical Fitness', 'Investigation', 'Communication'], 
        salary: '₹4-12 LPA',
        employers: [
          { name: 'State Police', logo: '', type: 'govt' },
          { name: 'Central Police Forces', logo: '', type: 'govt' },
          { name: 'CBI', logo: 'https://logo.clearbit.com/cbi.gov.in', type: 'govt' },
          { name: 'BSF/CRPF/CISF', logo: '', type: 'govt' },
          { name: 'IPS (UPSC)', logo: '', type: 'govt' }
        ]
      },
      { 
        title: 'Army Officer', 
        qualification: 'NDA / CDS', 
        description: 'Army officers lead soldiers in defending the nation and maintaining territorial integrity. They undergo training at prestigious academies like NDA and IMA. Officers plan operations, manage troops, and handle sophisticated equipment. Serving in infantry, artillery, engineering, or specialized corps, they face challenging terrains and situations. Army officers embody discipline, courage, and leadership. Their service includes combat, peacekeeping, and disaster relief, earning respect and honor.',
        skills: ['Leadership', 'Strategy', 'Physical Fitness', 'Decision Making'], 
        salary: '₹6-15 LPA',
        employers: [
          { name: 'Indian Army', logo: 'https://logo.clearbit.com/joinindianarmy.nic.in', type: 'govt' },
          { name: 'Indian Navy', logo: 'https://logo.clearbit.com/joinindiannavy.gov.in', type: 'govt' },
          { name: 'Indian Air Force', logo: 'https://logo.clearbit.com/indianairforce.nic.in', type: 'govt' },
          { name: 'NDA/IMA/OTA', logo: '', type: 'govt' },
          { name: 'Defense PSUs', logo: '', type: 'govt' }
        ]
      },
      { 
        title: 'Teacher', 
        qualification: 'Graduation + B.Ed', 
        description: 'Teachers shape future generations by imparting knowledge and values. They plan lessons, deliver engaging instruction, and assess student progress. Working in schools across rural and urban areas, they teach subjects from mathematics to languages. Teachers mentor students, manage classrooms, and participate in school activities. Government teachers enjoy job security and benefits. Their dedication builds foundational skills and inspires students to achieve their potential.',
        skills: ['Subject Knowledge', 'Communication', 'Patience', 'Classroom Management'], 
        salary: '₹3-10 LPA',
        employers: [
          { name: 'Kendriya Vidyalaya', logo: 'https://logo.clearbit.com/kvsangathan.nic.in', type: 'govt' },
          { name: 'Navodaya Vidyalaya', logo: '', type: 'govt' },
          { name: 'State Govt Schools', logo: '', type: 'govt' },
          { name: 'Private Schools', logo: '' },
          { name: 'Coaching Institutes', logo: '' }
        ]
      },
      { 
        title: 'Professor', 
        qualification: 'Post Graduation + NET', 
        description: 'Professors teach and conduct research at universities and colleges. They design curricula, deliver lectures, and guide students in advanced studies. Publishing papers and presenting at conferences, they contribute to academic knowledge. Professors mentor Ph.D. scholars, evaluate dissertations, and serve on academic committees. Working in government or private institutions, they enjoy intellectual freedom and impact. Their expertise shapes professionals across fields from engineering to humanities.',
        skills: ['Research', 'Teaching', 'Publication', 'Mentoring'], 
        salary: '₹6-20 LPA',
        employers: [
          { name: 'IITs/NITs', logo: '', type: 'govt' },
          { name: 'Central Universities', logo: '', type: 'govt' },
          { name: 'State Universities', logo: '', type: 'govt' },
          { name: 'Private Universities', logo: '' },
          { name: 'Research Institutions', logo: '', type: 'govt' }
        ]
      },
    ]
  },
  entrepreneurship: {
    name: 'Entrepreneurship',
    icon: Rocket,
    color: 'from-orange-500 to-red-600',
    careers: [
      { 
        title: 'Entrepreneur', 
        qualification: 'No fixed qualification', 
        description: 'Entrepreneurs identify opportunities and create businesses that solve problems. They develop business plans, secure funding, and build teams. From traditional retail to tech startups, entrepreneurs take calculated risks to bring ideas to market. They handle operations, marketing, finance, and customer relations. Success requires resilience, adaptability, and continuous learning. Entrepreneurs drive economic growth, create employment, and innovate industries, turning vision into reality through determination.',
        skills: ['Business Planning', 'Leadership', 'Risk Management', 'Networking'], 
        salary: 'Variable / Unlimited',
        employers: [
          { name: 'Own Business', logo: '' },
          { name: 'Startup Accelerators', logo: '' },
          { name: 'MSME Support', logo: '', type: 'govt' },
          { name: 'Startup India', logo: 'https://logo.clearbit.com/startupindia.gov.in', type: 'govt' },
          { name: 'Angel Investors/VCs', logo: '' }
        ]
      },
      { 
        title: 'Startup Founder', 
        qualification: 'Skill / Experience based', 
        description: 'Startup founders build innovative companies that disrupt markets and scale rapidly. They identify problems, validate solutions, and iterate products based on customer feedback. Founders pitch to investors, manage teams, and navigate the challenges of rapid growth. They work across technology, healthcare, fintech, and consumer sectors. With India\'s thriving startup ecosystem and supportive policies, founders access funding, mentorship, and global opportunities to build impactful ventures.',
        skills: ['Innovation', 'Product Development', 'Fundraising', 'Team Building'], 
        salary: 'Variable / Unlimited',
        employers: [
          { name: 'Own Startup', logo: '' },
          { name: 'Y Combinator', logo: 'https://logo.clearbit.com/ycombinator.com' },
          { name: 'Sequoia Capital', logo: 'https://logo.clearbit.com/sequoiacap.com' },
          { name: 'Accel Partners', logo: 'https://logo.clearbit.com/accel.com' },
          { name: 'IIM Incubators', logo: '' }
        ]
      },
    ]
  },
  entertainment: {
    name: 'Movies & Entertainment',
    icon: Star,
    color: 'from-yellow-500 to-amber-600',
    careers: [
      { 
        title: 'Film Director', 
        qualification: 'Film School / Experience', 
        description: 'Film directors bring creative visions to life on screen, overseeing all aspects of movie production. They work with actors to shape performances, collaborate with cinematographers for visual storytelling, and guide editors to achieve the final cut. Directors interpret scripts, make creative decisions, and manage large production teams. From independent films to blockbusters, they craft narratives that entertain, inspire, and provoke thought across audiences worldwide.',
        skills: ['Visual Storytelling', 'Leadership', 'Script Analysis', 'Team Management'], 
        salary: '₹5-100 LPA',
        employers: [
          { name: 'Yash Raj Films', logo: 'https://logo.clearbit.com/yashrajfilms.com' },
          { name: 'Dharma Productions', logo: '' },
          { name: 'Excel Entertainment', logo: '' },
          { name: 'Red Chillies', logo: '' },
          { name: 'Independent Films', logo: '' }
        ]
      },
      { 
        title: 'Music Composer', 
        qualification: 'Music Degree / Self-taught', 
        description: 'Music composers create original scores and soundtracks that enhance storytelling in films, TV shows, and advertisements. They understand musical theory, can play multiple instruments, and use digital audio workstations to produce high-quality compositions. Composers work closely with directors to capture emotions, build tension, and create memorable themes. From classical orchestration to electronic beats, their music becomes an integral part of visual narratives.',
        skills: ['Music Theory', 'DAW Software', 'Orchestration', 'Sound Design'], 
        salary: '₹3-50 LPA',
        employers: [
          { name: 'T-Series', logo: 'https://logo.clearbit.com/tseries.com' },
          { name: 'Sony Music', logo: 'https://logo.clearbit.com/sonymusic.com' },
          { name: 'Zee Music', logo: 'https://logo.clearbit.com/zeemusiccompany.com' },
          { name: 'Film Productions', logo: '' },
          { name: 'Ad Agencies', logo: '' }
        ]
      },
      { 
        title: 'Lyricist', 
        qualification: 'Creative Writing / Experience', 
        description: 'Lyricists craft words that resonate with audiences through songs. They write lyrics for films, albums, advertisements, and independent artists, capturing emotions, stories, and cultural moments in verse. Working with composers and singers, they ensure words fit melodies perfectly. Successful lyricists understand rhythm, rhyme schemes, and the art of storytelling through song, creating memorable lines that become part of popular culture.',
        skills: ['Creative Writing', 'Poetry', 'Language Mastery', 'Collaboration'], 
        salary: '₹2-30 LPA',
        employers: [
          { name: 'Film Productions', logo: '' },
          { name: 'Music Labels', logo: '' },
          { name: 'Ad Agencies', logo: '' },
          { name: 'Independent Artists', logo: '' },
          { name: 'Freelance', logo: '' }
        ]
      },
      { 
        title: 'Film Editor', 
        qualification: 'Film School / Technical Training', 
        description: 'Film editors shape raw footage into compelling narratives, working closely with directors to realize their vision. They select the best takes, arrange sequences, adjust pacing, and add transitions to create seamless storytelling. Editors use professional software like Avid, Premiere Pro, and DaVinci Resolve. Their decisions on timing and rhythm directly impact how audiences experience films, making them crucial creative partners in filmmaking.',
        skills: ['Video Editing Software', 'Storytelling', 'Attention to Detail', 'Color Grading'], 
        salary: '₹4-25 LPA',
        employers: [
          { name: 'Film Productions', logo: '' },
          { name: 'Post-production Studios', logo: '' },
          { name: 'TV Channels', logo: '' },
          { name: 'OTT Platforms', logo: '' },
          { name: 'Ad Agencies', logo: '' }
        ]
      },
      { 
        title: 'Cinematographer', 
        qualification: 'Film School / Apprenticeship', 
        description: 'Cinematographers, or Directors of Photography (DOP), create the visual language of films through camera work and lighting. They select cameras, lenses, and lighting setups to achieve desired aesthetics. Working with directors, they translate scripts into visual imagery, setting mood, atmosphere, and style. Cinematographers lead camera teams, plan shots, and ensure technical excellence while maintaining artistic vision throughout production.',
        skills: ['Camera Operations', 'Lighting Design', 'Visual Composition', 'Color Theory'], 
        salary: '₹5-40 LPA',
        employers: [
          { name: 'Film Productions', logo: '' },
          { name: 'Ad Films', logo: '' },
          { name: 'Music Videos', logo: '' },
          { name: 'Documentary Films', logo: '' },
          { name: 'OTT Platforms', logo: '' }
        ]
      },
      { 
        title: 'Sound Engineer', 
        qualification: 'Audio Engineering / Technical Training', 
        description: 'Sound engineers capture, mix, and master audio for films, music, and live events. They operate recording equipment, adjust sound levels, and ensure audio quality meets professional standards. Working in studios, on film sets, or at concerts, they handle microphones, mixing consoles, and digital audio workstations. Sound engineers create immersive audio experiences, from crystal-clear dialogue to thundering sound effects.',
        skills: ['Audio Equipment', 'Mixing & Mastering', 'DAW Software', 'Acoustics'], 
        salary: '₹3-20 LPA',
        employers: [
          { name: 'Recording Studios', logo: '' },
          { name: 'Film Productions', logo: '' },
          { name: 'TV Channels', logo: '' },
          { name: 'Event Companies', logo: '' },
          { name: 'Radio Stations', logo: '' }
        ]
      },
      { 
        title: 'VFX Artist', 
        qualification: 'Animation/VFX Degree', 
        description: 'VFX artists create visual effects that make impossible scenes possible on screen. From superhero powers to alien worlds, they use software like Maya, Nuke, and Houdini to generate computer-generated imagery that blends seamlessly with live footage. They work on compositing, 3D modeling, motion tracking, and simulation. With increasing use of VFX in Indian cinema, skilled artists are in high demand across film and advertising industries.',
        skills: ['3D Modeling', 'Compositing', 'Motion Graphics', 'VFX Software'], 
        salary: '₹4-30 LPA',
        employers: [
          { name: 'Prime Focus', logo: 'https://logo.clearbit.com/primefocus.com' },
          { name: 'Red Chillies VFX', logo: '' },
          { name: 'DNEG', logo: 'https://logo.clearbit.com/dneg.com' },
          { name: 'Framestore', logo: 'https://logo.clearbit.com/framestore.com' },
          { name: 'MPC', logo: '' }
        ]
      },
      { 
        title: 'Actor', 
        qualification: 'Drama School / Natural Talent', 
        description: 'Actors bring characters to life through performance, expressing emotions and telling stories through their craft. They audition for roles, memorize scripts, develop characters, and work with directors to deliver compelling performances. From theater to films to digital content, actors entertain and inspire audiences. Success requires dedication, resilience, continuous skill development, and the ability to handle both fame and rejection gracefully.',
        skills: ['Acting Techniques', 'Voice Modulation', 'Expression', 'Physical Movement'], 
        salary: '₹1-100 LPA+',
        employers: [
          { name: 'Film Productions', logo: '' },
          { name: 'Theater Companies', logo: '' },
          { name: 'TV Channels', logo: '' },
          { name: 'OTT Platforms', logo: '' },
          { name: 'Ad Films', logo: '' }
        ]
      },
      { 
        title: 'Production Manager', 
        qualification: 'Film/Event Management', 
        description: 'Production managers oversee the logistical and administrative aspects of film and TV productions. They create schedules, manage budgets, coordinate crew, arrange locations, and ensure productions run smoothly. Working between creative and business teams, they solve problems, handle emergencies, and keep projects on track. Their organizational skills and ability to manage complex operations are essential for successful productions.',
        skills: ['Project Management', 'Budgeting', 'Coordination', 'Problem Solving'], 
        salary: '₹4-20 LPA',
        employers: [
          { name: 'Film Productions', logo: '' },
          { name: 'TV Channels', logo: '' },
          { name: 'OTT Platforms', logo: '' },
          { name: 'Ad Agencies', logo: '' },
          { name: 'Event Companies', logo: '' }
        ]
      },
      { 
        title: 'Screenwriter', 
        qualification: 'Creative Writing / Film School', 
        description: 'Screenwriters craft the stories and dialogue that form the foundation of films and TV shows. They create original scripts or adapt existing works, developing characters, plot, and dialogue that engage audiences. Writers may work alone or in writers\' rooms, revising drafts based on feedback. Successful screenwriters understand dramatic structure, character development, and visual storytelling, bringing ideas to life through the written word.',
        skills: ['Storytelling', 'Dialogue Writing', 'Script Formatting', 'Character Development'], 
        salary: '₹3-40 LPA',
        employers: [
          { name: 'Film Productions', logo: '' },
          { name: 'OTT Platforms', logo: '' },
          { name: 'TV Channels', logo: '' },
          { name: 'Content Studios', logo: '' },
          { name: 'Freelance', logo: '' }
        ]
      },
    ]
  },
};

// Course category icons and colors
const CATEGORY_CONFIG = {
  programming: { icon: Code2, color: 'from-violet-500 to-purple-600', label: 'Programming' },
  life_skills: { icon: Users, color: 'from-emerald-500 to-teal-600', label: 'Life Skills' },
  financial: { icon: Calculator, color: 'from-green-500 to-emerald-600', label: 'Financial' },
  career: { icon: Briefcase, color: 'from-blue-500 to-indigo-600', label: 'Career' },
  safety: { icon: Shield, color: 'from-orange-500 to-red-600', label: 'Safety' },
  environment: { icon: Leaf, color: 'from-lime-500 to-green-600', label: 'Environment' },
  competitive_exams: { icon: Trophy, color: 'from-amber-500 to-orange-600', label: 'Competitive Exams' },
};

// Header component
const ExplorerHeader = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <header className="glass sticky top-0 z-50 border-b border-border px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 hover:opacity-80">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Code2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold">CognitiaIndia</span>
          </button>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/profile')}>
            <User className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={logout}>
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
};

// Career Card Component with Image
const CareerCard = ({ career, onSelect }) => {
  const careerImage = CAREER_IMAGES[career.title];
  
  return (
    <div 
      onClick={() => onSelect(career)}
      className="group cursor-pointer rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-xl transition-all duration-300 overflow-hidden"
      data-testid={`career-card-${career.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Career Image */}
      <div className="relative h-40 bg-gradient-to-br from-muted to-muted/50 overflow-hidden">
        {careerImage ? (
          <img 
            src={careerImage} 
            alt={career.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl font-bold text-muted-foreground/30">
            {career.title.charAt(0)}
          </div>
        )}
        {/* Salary Badge Overlay */}
        <div className="absolute top-3 right-3">
          <Badge className="bg-green-500 text-white border-0 shadow-lg font-semibold">
            <IndianRupee className="w-3 h-3 mr-1" />
            {career.salary.replace('₹', '')}
          </Badge>
        </div>
      </div>
      
      {/* Career Info */}
      <div className="p-4">
        <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">{career.title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{career.qualification}</p>
        <div className="flex flex-wrap gap-1 mt-3">
          {career.skills.slice(0, 3).map((skill, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

// Career Detail Modal with Image
const CareerDetailModal = ({ career, category, onClose }) => {
  if (!career) return null;
  
  const CategoryIcon = category?.icon || Briefcase;
  const careerImage = CAREER_IMAGES[career.title];
  
  return (
    <Dialog open={!!career} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0">
        {/* Hero Image Section */}
        <div className="relative h-48 bg-gradient-to-br from-muted to-muted/50">
          {careerImage ? (
            <img 
              src={careerImage} 
              alt={career.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${category?.color || 'from-primary to-secondary'} flex items-center justify-center`}>
              <CategoryIcon className="w-20 h-20 text-white/50" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          <div className="absolute bottom-4 left-6 right-6">
            <h2 className="text-2xl font-bold text-white drop-shadow-lg">{career.title}</h2>
            <p className="text-white/80 drop-shadow">{career.qualification}</p>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Salary Highlight */}
          <div className="flex items-center justify-center gap-4 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
              <IndianRupee className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Expected Annual Salary</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{career.salary}</div>
            </div>
          </div>
          
          {/* About */}
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> About This Career
            </h4>
            <p className="text-muted-foreground">{career.description}</p>
          </div>
          
          {/* Skills Required */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Award className="w-4 h-4" /> Key Skills Required
            </h4>
            <div className="flex flex-wrap gap-2">
              {career.skills.map((skill, idx) => (
                <Badge key={idx} variant="secondary" className="px-3 py-1">{skill}</Badge>
              ))}
            </div>
          </div>
          
          {/* Qualification */}
          <div className="p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="w-4 h-4 text-primary" />
              <span className="font-semibold">Required Qualification</span>
            </div>
            <p className="text-lg">{career.qualification}</p>
          </div>
          
          {/* Employers Section */}
          {career.employers && career.employers.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Top Employers
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {career.employers.map((employer, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center gap-2 p-3 rounded-lg border ${
                      employer.type === 'govt' 
                        ? 'bg-blue-500/5 border-blue-500/20' 
                        : 'bg-card border-border'
                    }`}
                  >
                    {employer.logo ? (
                      <img 
                        src={employer.logo} 
                        alt={employer.name}
                        className="w-8 h-8 object-contain rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold ${
                        employer.type === 'govt' ? 'bg-blue-500/10 text-blue-600' : 'bg-muted text-muted-foreground'
                      }`}>
                        {employer.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium truncate block">{employer.name}</span>
                      {employer.type === 'govt' && (
                        <span className="text-xs text-blue-600">Govt. Sector</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
            <Button className="flex-1">
              <BookOpen className="w-4 h-4 mr-2" /> Explore Related Courses
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Course Card Component
const CourseCard = ({ course, onSelect, onEnroll }) => {
  const config = CATEGORY_CONFIG[course.category] || CATEGORY_CONFIG.programming;
  const IconComponent = config.icon;
  
  return (
    <div 
      className="group cursor-pointer p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
      data-testid={`course-card-${course.course_id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <IconComponent className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate">{course.title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{course.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              {course.has_code_lab ? '14 Chapters' : '12 Chapters'}
            </span>
            <Badge variant="outline" className="text-xs capitalize">{course.difficulty}</Badge>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button variant="outline" size="sm" className="flex-1" onClick={(e) => { e.stopPropagation(); onSelect(course); }}>
          View Details
        </Button>
        <Button size="sm" className="flex-1" onClick={(e) => { e.stopPropagation(); onEnroll(course); }}>
          Enroll
        </Button>
      </div>
    </div>
  );
};

// Course List Row Component
const CourseListRow = ({ course, onSelect, onEnroll }) => {
  const config = CATEGORY_CONFIG[course.category] || CATEGORY_CONFIG.programming;
  const IconComponent = config.icon;
  
  return (
    <div 
      className="flex items-center gap-4 p-4 rounded-lg bg-card border border-border hover:border-primary/50 hover:bg-muted/30 transition-all cursor-pointer"
      onClick={() => onSelect(course)}
      data-testid={`course-row-${course.course_id}`}
    >
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center`}>
        <IconComponent className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium truncate">{course.title}</h3>
      </div>
      <div className="hidden md:block w-24 text-sm text-muted-foreground capitalize">{course.category.replace('_', ' ')}</div>
      <div className="hidden md:block w-20 text-sm text-muted-foreground capitalize">{course.difficulty}</div>
      <div className="hidden md:block w-20 text-sm text-muted-foreground">{course.has_code_lab ? '14' : '12'} Ch</div>
      <Button size="sm" onClick={(e) => { e.stopPropagation(); onEnroll(course); }}>
        Enroll
      </Button>
    </div>
  );
};

// Course Detail Modal
const CourseDetailModal = ({ course, onClose, onEnroll }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chapterTitles, setChapterTitles] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [teacherVisible, setTeacherVisible] = useState(false);
  const [teacherDismissed, setTeacherDismissed] = useState(false);
  const [teacherText, setTeacherText] = useState('');
  const [teacherKey, setTeacherKey] = useState('');
  
  useEffect(() => {
    if (course?.course_id) {
      setLoadingChapters(true);
      coursesApi.getChapterTitles(course.course_id)
        .then(data => setChapterTitles(data))
        .catch(() => setChapterTitles([]))
        .finally(() => setLoadingChapters(false));
    }
  }, [course?.course_id]);

  const handleChapterClick = (ch, idx) => {
    setTeacherText(`Chapter ${idx + 1}: ${ch.title}. Let me tell you about this topic. ${course.title} covers ${ch.title} to help students build practical knowledge and skills.`);
    setTeacherKey(`course-${course.course_id}-ch-${idx}`);
    setTeacherVisible(true);
  };
  
  if (!course) return null;
  
  const config = CATEGORY_CONFIG[course.category] || CATEGORY_CONFIG.programming;
  const IconComponent = config.icon;
  
  const useCases = course.has_code_lab
    ? ['Web Development', 'Mobile Apps', 'Data Science', 'Automation', 'Game Development', 'AI/ML Applications']
    : ['Personal Growth', 'Career Advancement', 'Daily Life', 'Professional Settings', 'Community Impact', 'Future Planning'];
  
  return (
    <>
    <Dialog open={!!course} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <div>
              <DialogTitle className="text-2xl">{course.title}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="capitalize">{config.label}</Badge>
                <Badge variant="secondary" className="capitalize">{course.difficulty}</Badge>
              </div>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Description */}
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> About This Course
            </h4>
            <p className="text-muted-foreground">{course.description}</p>
          </div>
          
          {/* Chapters */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <GraduationCap className="w-4 h-4" /> Course Chapters
            </h4>
            {loadingChapters ? (
              <div className="text-sm text-muted-foreground">Loading chapters...</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {chapterTitles.map((ch, idx) => (
                  <div key={idx} onClick={() => handleChapterClick(ch, idx)}
                    className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors" data-testid={`chapter-item-${idx}`}>
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                      {idx + 1}
                    </span>
                    <span className="text-sm">{ch.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Where This Skill Is Useful */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> Where This Skill Is Useful
            </h4>
            <div className="flex flex-wrap gap-2">
              {useCases.map((use, idx) => (
                <Badge key={idx} variant="secondary" className="px-3 py-1">{use}</Badge>
              ))}
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-muted/50 text-center">
              <BookOpen className="w-6 h-6 mx-auto mb-2 text-primary" />
              <div className="text-xl font-bold">{course.has_code_lab ? '14' : '12'}</div>
              <div className="text-xs text-muted-foreground">Chapters</div>
            </div>
            <div className="p-4 rounded-xl bg-muted/50 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-primary" />
              <div className="text-xl font-bold">{course.has_code_lab ? '30' : '25'}</div>
              <div className="text-xs text-muted-foreground">Exam Questions</div>
            </div>
            <div className="p-4 rounded-xl bg-muted/50 text-center">
              <Award className="w-6 h-6 mx-auto mb-2 text-primary" />
              <div className="text-xl font-bold">60%</div>
              <div className="text-xs text-muted-foreground">Passing Score</div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
            <Button className="flex-1" onClick={() => onEnroll(course)}>
              <Play className="w-4 h-4 mr-2" /> Start Learning
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <TeacherAvatar visible={teacherVisible} text={teacherText} cacheKey={teacherKey}
      avatarId={user?.avatar_id || 'male-1'}
      onClose={() => { setTeacherVisible(false); setTeacherDismissed(true); }}
      dismissed={teacherDismissed}
      onResummon={() => { setTeacherVisible(true); setTeacherDismissed(false); }} />
    </>
  );
};

// Main Explorer Page
const Explorer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('courses'); // 'courses' or 'careers'
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('title'); // 'title', 'category', 'difficulty'
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Course explorer state
  const [selectedCourseCategory, setSelectedCourseCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['programming']);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  // Career explorer state
  const [selectedCareerCategory, setSelectedCareerCategory] = useState('engineering');
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [allowedCourseIds, setAllowedCourseIds] = useState(null);
  const [gradeRequired, setGradeRequired] = useState(false);
  
  // Fetch courses
  useEffect(() => {
    fetchCourses();
    // Fetch allowed content for grade filtering
    gradeAllocationsApi.getStudentContent().then(data => {
      if (data.grade_required) {
        setGradeRequired(true);
        return;
      }
      if (data.allowed_courses && data.allowed_courses !== 'all') {
        setAllowedCourseIds(data.allowed_courses);
      }
    }).catch(() => {});
  }, []);
  
  const fetchCourses = async () => {
    try {
      const data = await coursesApi.getAll();
      setCourses(data.filter(c => c.is_active));
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };
  
  // Group courses by category
  const coursesByCategory = useMemo(() => {
    const grouped = {};
    const source = allowedCourseIds ? courses.filter(c => allowedCourseIds.includes(c.course_id)) : courses;
    source.forEach(course => {
      const cat = course.category || 'programming';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(course);
    });
    return grouped;
  }, [courses, allowedCourseIds]);
  
  // Filter and sort courses
  const filteredCourses = useMemo(() => {
    let filtered = allowedCourseIds ? courses.filter(c => allowedCourseIds.includes(c.course_id)) : courses;
    if (selectedCourseCategory) filtered = filtered.filter(c => c.category === selectedCourseCategory);
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.title.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
      );
    }
    
    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'title') cmp = a.title.localeCompare(b.title);
      else if (sortBy === 'category') cmp = (a.category || '').localeCompare(b.category || '');
      else if (sortBy === 'difficulty') {
        const order = { beginner: 1, intermediate: 2, advanced: 3 };
        cmp = (order[a.difficulty] || 0) - (order[b.difficulty] || 0);
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    
    return filtered;
  }, [courses, selectedCourseCategory, searchQuery, sortBy, sortOrder]);
  
  // Toggle category expansion
  const toggleCategory = (cat) => {
    setExpandedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };
  
  // Handle course enrollment — instant, no approval needed.
  const handleEnroll = async (course) => {
    try {
      await courseRequestsApi.create(course.course_id);
      toast.success('Enrolled! You can start learning right away.');
      navigate('/courses');
    } catch (error) {
      toast.error(error.message || 'Failed to enroll');
    }
  };
  
  // Toggle sort
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };
  
  if (gradeRequired) {
    return (
      <div className="min-h-screen bg-background" data-testid="explorer-page">
        <ExplorerHeader />
        <div className="flex items-center justify-center min-h-[60vh] px-6">
          <div className="max-w-md w-full text-center p-8 rounded-2xl border-2 border-yellow-500/30 bg-card shadow-xl" data-testid="explorer-grade-required">
            <div className="w-16 h-16 rounded-2xl bg-yellow-500/10 flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-yellow-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">Grade Required</h2>
            <p className="text-muted-foreground text-sm mb-6">
              Please set your grade first to access courses. Go to Visual Labs to set your grade.
            </p>
            <Button onClick={() => navigate('/labs')} data-testid="go-set-grade-btn">Set Grade Now</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="explorer-page">
      <ExplorerHeader />
      
      <div className="flex">
        {/* Left Sidebar - Tree View */}
        <aside className="w-72 min-h-[calc(100vh-64px)] border-r border-border bg-card/50 p-4 hidden lg:block">
          <div className="sticky top-20">
            {/* Tab Selector */}
            <div className="flex gap-1 p-1 bg-muted rounded-lg mb-6">
              <button
                onClick={() => setActiveTab('courses')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'courses' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'
                }`}
              >
                <BookOpen className="w-4 h-4 inline mr-2" />
                Courses
              </button>
              <button
                onClick={() => setActiveTab('careers')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'careers' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'
                }`}
              >
                <Briefcase className="w-4 h-4 inline mr-2" />
                Careers
              </button>
            </div>
            
            {activeTab === 'courses' ? (
              <>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">CATEGORIES</h3>
                <nav className="space-y-1">
                  <button
                    onClick={() => setSelectedCourseCategory(null)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                      !selectedCourseCategory ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    <Grid3X3 className="w-4 h-4" />
                    <span>All Courses</span>
                    <Badge variant="secondary" className="ml-auto">{courses.length}</Badge>
                  </button>
                  
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                    const count = coursesByCategory[key]?.length || 0;
                    const isExpanded = expandedCategories.includes(key);
                    const IconComponent = config.icon;
                    
                    return (
                      <div key={key}>
                        <button
                          onClick={() => {
                            setSelectedCourseCategory(key);
                            toggleCategory(key);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                            selectedCourseCategory === key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          }`}
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <IconComponent className="w-4 h-4" />
                          <span className="flex-1">{config.label}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </button>
                        
                        {isExpanded && count > 0 && (
                          <div className="ml-8 mt-1 space-y-1">
                            {coursesByCategory[key]?.slice(0, 5).map(course => (
                              <button
                                key={course.course_id}
                                onClick={() => setSelectedCourse(course)}
                                className="w-full text-left text-sm px-3 py-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground truncate"
                              >
                                {course.title}
                              </button>
                            ))}
                            {count > 5 && (
                              <span className="text-xs text-muted-foreground px-3">+{count - 5} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </>
            ) : (
              <>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">CAREER FIELDS</h3>
                <nav className="space-y-1">
                  {Object.entries(CAREER_DATA).map(([key, data]) => {
                    const IconComponent = data.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedCareerCategory(key)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                          selectedCareerCategory === key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                        <span className="flex-1 truncate">{data.name}</span>
                        <Badge variant="secondary">{data.careers.length}</Badge>
                      </button>
                    );
                  })}
                </nav>
              </>
            )}
          </div>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 p-6">
          {activeTab === 'courses' ? (
            <>
              {/* Course Explorer Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold">
                    {selectedCourseCategory 
                      ? CATEGORY_CONFIG[selectedCourseCategory]?.label || 'Courses'
                      : 'All Courses'
                    }
                  </h1>
                  <p className="text-muted-foreground">
                    {filteredCourses.length} courses available
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search courses..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  
                  {/* View Toggle */}
                  <div className="flex gap-1 p-1 bg-muted rounded-lg">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`p-2 rounded ${viewMode === 'card' ? 'bg-background shadow' : ''}`}
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded ${viewMode === 'list' ? 'bg-background shadow' : ''}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Sort Options (for list view) */}
              {viewMode === 'list' && (
                <div className="flex items-center gap-4 px-4 py-2 mb-4 bg-muted/50 rounded-lg text-sm font-medium">
                  <div className="w-10" />
                  <button onClick={() => toggleSort('title')} className="flex-1 flex items-center gap-1 hover:text-primary">
                    Title <ArrowUpDown className="w-3 h-3" />
                  </button>
                  <button onClick={() => toggleSort('category')} className="hidden md:flex w-24 items-center gap-1 hover:text-primary">
                    Category <ArrowUpDown className="w-3 h-3" />
                  </button>
                  <button onClick={() => toggleSort('difficulty')} className="hidden md:flex w-20 items-center gap-1 hover:text-primary">
                    Difficulty <ArrowUpDown className="w-3 h-3" />
                  </button>
                  <div className="hidden md:block w-20">Chapters</div>
                  <div className="w-20" />
                </div>
              )}
              
              {/* Course Grid/List */}
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} className="h-48 rounded-xl bg-muted animate-pulse" />
                  ))}
                </div>
              ) : viewMode === 'card' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredCourses.map(course => (
                    <CourseCard 
                      key={course.course_id} 
                      course={course} 
                      onSelect={setSelectedCourse}
                      onEnroll={handleEnroll}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredCourses.map(course => (
                    <CourseListRow 
                      key={course.course_id} 
                      course={course} 
                      onSelect={setSelectedCourse}
                      onEnroll={handleEnroll}
                    />
                  ))}
                </div>
              )}
              
              {filteredCourses.length === 0 && !loading && (
                <div className="text-center py-16">
                  <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">No courses found</h3>
                  <p className="text-muted-foreground">Try adjusting your search or filters</p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Career Explorer Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  {selectedCareerCategory && CAREER_DATA[selectedCareerCategory] && (
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${CAREER_DATA[selectedCareerCategory].color} flex items-center justify-center`}>
                      {React.createElement(CAREER_DATA[selectedCareerCategory].icon, { className: 'w-6 h-6 text-white' })}
                    </div>
                  )}
                  <div>
                    <h1 className="text-2xl font-bold">
                      {CAREER_DATA[selectedCareerCategory]?.name || 'Career Explorer'}
                    </h1>
                    <p className="text-muted-foreground">
                      Explore {CAREER_DATA[selectedCareerCategory]?.careers.length || 0} career paths
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Career Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {CAREER_DATA[selectedCareerCategory]?.careers.map((career, idx) => (
                  <CareerCard 
                    key={idx} 
                    career={career} 
                    onSelect={setSelectedCareer}
                  />
                ))}
              </div>
            </>
          )}
        </main>
      </div>
      
      {/* Course Detail Modal */}
      <CourseDetailModal 
        course={selectedCourse} 
        onClose={() => setSelectedCourse(null)}
        onEnroll={handleEnroll}
      />
      
      {/* Career Detail Modal */}
      <CareerDetailModal 
        career={selectedCareer}
        category={selectedCareerCategory ? CAREER_DATA[selectedCareerCategory] : null}
        onClose={() => setSelectedCareer(null)}
      />
    </div>
  );
};

export default Explorer;
